import { useCallback, useEffect, useState } from 'react';
import { fetchMentorCatalog } from '../../infrastructure/api/mentors-list.api';
import type {
  MentorCatalogCategory,
  MentorCatalogItem,
} from '../../application/types';

const PAGE_LIMIT = 6;

export function useMentorCatalog(categoryId: number | null) {
  const [items, setItems] = useState<MentorCatalogItem[]>([]);
  const [categories, setCategories] = useState<MentorCatalogCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    fetchMentorCatalog({ limit: PAGE_LIMIT, categoryId })
      .then((page) => {
        if (cancelled) return;
        setCategories(page.categories);
        setItems(page.mentors.items);
        setTotal(page.mentors.total);
        setNextCursor(page.mentors.nextCursor);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setNextCursor(null);
        setIsError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  const loadMore = useCallback(() => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    fetchMentorCatalog({
      limit: PAGE_LIMIT,
      categoryId,
      cursor: nextCursor,
    })
      .then((page) => {
        setItems((current) => [...current, ...page.mentors.items]);
        setTotal(page.mentors.total);
        setNextCursor(page.mentors.nextCursor);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [categoryId, isLoadingMore, nextCursor]);

  return {
    items,
    categories,
    total,
    nextCursor,
    isLoading,
    isLoadingMore,
    isError,
    loadMore,
  };
}
